/**
 * R232q5aM: Keybinds store — WoW WotLK 3.3.5a parity.
 *
 * Single source of truth for every rebindable action in AIWARI. World.tsx
 * + ActionBar consume `useKeybinds(...).resolveKey(action)` instead of
 * hard-coding key strings.
 *
 * Categories (UI tabs):
 *   movement — forward/back/strafe/jump/auto-run/walk
 *   combat   — attack, target nearest, target self, focus, cleartarget,
 *              hotbar slots 1-12, shift-1..0, ctrl-1..0, alt-1..0
 *   ui       — chat, microMenu, settings, macros, keybinds, map, inventory,
 *              spellbook, talents, character, social, achievements,
 *              questLog, mailbox, calendar
 *   macros   — first 18 macro slots (player scope) + character 18 slots
 *
 * Keys are stored as canonical strings: '<MODS>+<MAIN>' upper-cased.
 *   examples:    '1', 'F1', 'TAB', 'SHIFT+1', 'CTRL+ALT+M'
 *
 * Detection of conflicts is naive — same key string bound to two actions
 * triggers a warning toast inside KeybindEditor.
 */

import { create } from 'zustand';

const STORAGE_KEY = 'aiwari-keybinds-v1';

/* ─────────────── action catalogue ─────────────── */

export type KeybindCategory = 'movement' | 'combat' | 'ui' | 'macros';

export interface KeybindAction {
  id: string;
  category: KeybindCategory;
  /** Human-readable label, RU. */
  label: string;
  /** Default key string. */
  defaultKey: string;
}

/**
 * WoW WotLK 3.3.5a default keymap (mostly). 1-9 / 0 / - / = for hotbar 1..12,
 * Shift+ / Ctrl+ / Alt+ stacks for additional bars (used by future
 * MultiBars feature). Movement uses both WASD and arrow keys — for arrows
 * we add second-binding support via secondaryKey; primaryKey is what the
 * UI shows by default.
 */
export const ALL_ACTIONS: KeybindAction[] = [
  // Movement
  { id: 'move.forward',     category: 'movement', label: 'Вперёд',           defaultKey: 'W' },
  { id: 'move.back',        category: 'movement', label: 'Назад',            defaultKey: 'S' },
  { id: 'move.strafeLeft',  category: 'movement', label: 'Шаг влево',        defaultKey: 'Q' },
  { id: 'move.strafeRight', category: 'movement', label: 'Шаг вправо',       defaultKey: 'E' },
  { id: 'move.turnLeft',    category: 'movement', label: 'Поворот влево',    defaultKey: 'A' },
  { id: 'move.turnRight',   category: 'movement', label: 'Поворот вправо',   defaultKey: 'D' },
  { id: 'move.jump',        category: 'movement', label: 'Прыжок',           defaultKey: ' ' },
  { id: 'move.autorun',     category: 'movement', label: 'Авто-бег',         defaultKey: 'NUMLOCK' },
  { id: 'move.walk',        category: 'movement', label: 'Шаг',              defaultKey: '/' },

  // Combat — generic
  { id: 'combat.targetNearest', category: 'combat', label: 'Ближайший враг',     defaultKey: 'TAB' },
  { id: 'combat.targetSelf',    category: 'combat', label: 'Цель — я',           defaultKey: 'F1' },
  { id: 'combat.focusTarget',   category: 'combat', label: 'Закрепить цель',     defaultKey: 'CTRL+F' },
  { id: 'combat.clearTarget',   category: 'combat', label: 'Сбросить цель',      defaultKey: 'ESCAPE' },
  { id: 'combat.attack',        category: 'combat', label: 'Авто-атака',         defaultKey: 'T' },
  { id: 'combat.assist',        category: 'combat', label: 'Помочь товарищу',    defaultKey: 'F' },

  // Combat — hotbar slots (1..12)
  ...hotbarActions('hotbar.bar1', '', ['1','2','3','4','5','6','7','8','9','0','-','=']),
  // Bar2 = Shift+
  ...hotbarActions('hotbar.bar2', 'SHIFT+', ['1','2','3','4','5','6','7','8','9','0','-','=']),
  // Bar3 = Ctrl+
  ...hotbarActions('hotbar.bar3', 'CTRL+', ['1','2','3','4','5','6','7','8','9','0','-','=']),
  // Bar4 = Alt+
  ...hotbarActions('hotbar.bar4', 'ALT+', ['1','2','3','4','5','6','7','8','9','0','-','=']),

  // UI panels (matches existing components)
  { id: 'ui.chat',         category: 'ui', label: 'Открыть чат',           defaultKey: 'ENTER' },
  { id: 'ui.microMenu',    category: 'ui', label: 'Главное меню',          defaultKey: 'ESCAPE' },
  { id: 'ui.settings',     category: 'ui', label: 'Настройки',             defaultKey: 'CTRL+,' },
  { id: 'ui.macros',       category: 'ui', label: 'Макросы',               defaultKey: 'SHIFT+M' },
  { id: 'ui.keybinds',     category: 'ui', label: 'Назначение клавиш',     defaultKey: 'CTRL+K' },
  { id: 'ui.map',          category: 'ui', label: 'Карта мира',            defaultKey: 'M' },
  { id: 'ui.inventory',    category: 'ui', label: 'Инвентарь',             defaultKey: 'B' },
  { id: 'ui.spellbook',    category: 'ui', label: 'Книга заклинаний',      defaultKey: 'P' },
  { id: 'ui.talents',      category: 'ui', label: 'Таланты',               defaultKey: 'N' },
  { id: 'ui.character',    category: 'ui', label: 'Персонаж',              defaultKey: 'C' },
  { id: 'ui.social',       category: 'ui', label: 'Социальное',            defaultKey: 'O' },
  { id: 'ui.achievements', category: 'ui', label: 'Достижения',            defaultKey: 'Y' },
  { id: 'ui.questLog',     category: 'ui', label: 'Журнал заданий',        defaultKey: 'L' },
  { id: 'ui.mailbox',      category: 'ui', label: 'Почта',                 defaultKey: 'CTRL+M' },
  { id: 'ui.calendar',     category: 'ui', label: 'Календарь',             defaultKey: 'CTRL+C' },
  { id: 'ui.help',         category: 'ui', label: 'Помощь',                defaultKey: 'F12' },

  // Macros — first 6 player slots get keybinds by default; rest blank.
  ...Array.from({ length: 18 }, (_, i): KeybindAction => ({
    id: `macros.player.${i}`,
    category: 'macros',
    label: `Макрос игрока #${i + 1}`,
    defaultKey: '',
  })),
  ...Array.from({ length: 18 }, (_, i): KeybindAction => ({
    id: `macros.character.${i}`,
    category: 'macros',
    label: `Макрос персонажа #${i + 1}`,
    defaultKey: '',
  })),
];

function hotbarActions(prefix: string, mod: string, keys: string[]): KeybindAction[] {
  return keys.map((k, i): KeybindAction => ({
    id: `${prefix}.${i + 1}`,
    category: 'combat',
    label: `${barLabel(prefix)} — слот ${i + 1}`,
    defaultKey: `${mod}${k}`.toUpperCase(),
  }));
}

function barLabel(prefix: string): string {
  if (prefix.endsWith('.bar1')) return 'Панель 1';
  if (prefix.endsWith('.bar2')) return 'Панель 2 (Shift)';
  if (prefix.endsWith('.bar3')) return 'Панель 3 (Ctrl)';
  if (prefix.endsWith('.bar4')) return 'Панель 4 (Alt)';
  return prefix;
}

/* ─────────────── store ─────────────── */

export type KeybindMap = Record<string, string>;

interface KeybindStore {
  /** actionId → key string. Missing entries fall back to defaultKey. */
  bindings: KeybindMap;
  /** Set or clear a binding (empty key = unbound). */
  setBinding: (actionId: string, key: string) => void;
  /** Reset every binding to defaults. */
  reset: () => void;
  /** Resolve a binding (current → default fallback). */
  resolveKey: (actionId: string) => string;
  /** Find conflicts: groups of actionIds bound to the same key. */
  conflicts: () => Array<{ key: string; ids: string[] }>;
  /** Reverse lookup: which actionId is bound to this canonical key string?
   *  If multiple, returns the first. Returns '' if none. */
  resolveAction: (key: string) => string;
}

function loadBindings(): KeybindMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as KeybindMap;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch { return {}; }
}
function saveBindings(b: KeybindMap) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(b)); } catch {}
}

export const useKeybinds = create<KeybindStore>((set, get) => ({
  bindings: loadBindings(),
  setBinding: (actionId, key) => {
    const cur = { ...get().bindings };
    if (!key) delete cur[actionId];
    else cur[actionId] = key.toUpperCase();
    saveBindings(cur);
    set({ bindings: cur });
  },
  reset: () => {
    saveBindings({});
    set({ bindings: {} });
  },
  resolveKey: (actionId) => {
    const cur = get().bindings;
    if (cur[actionId] != null) return cur[actionId]!;
    const def = ALL_ACTIONS.find((a) => a.id === actionId);
    return def?.defaultKey ?? '';
  },
  conflicts: () => {
    const map = new Map<string, string[]>();
    for (const a of ALL_ACTIONS) {
      const k = get().resolveKey(a.id);
      if (!k) continue;
      const arr = map.get(k) ?? [];
      arr.push(a.id);
      map.set(k, arr);
    }
    const out: Array<{ key: string; ids: string[] }> = [];
    for (const [key, ids] of map) {
      if (ids.length > 1) out.push({ key, ids });
    }
    return out;
  },
  resolveAction: (key) => {
    if (!key) return '';
    const norm = key.toUpperCase();
    for (const a of ALL_ACTIONS) {
      if (get().resolveKey(a.id).toUpperCase() === norm) return a.id;
    }
    return '';
  },
}));

/* ─────────────── helpers ─────────────── */

/** Convert KeyboardEvent → canonical key string used by the store. */
export function eventToKey(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.ctrlKey)  parts.push('CTRL');
  if (e.altKey)   parts.push('ALT');
  if (e.shiftKey) parts.push('SHIFT');
  let k = e.key;
  if (k === ' ') k = 'SPACE';
  else if (k.length === 1) k = k.toUpperCase();
  else k = k.toUpperCase();
  parts.push(k);
  return parts.join('+');
}

/** True if a KeyboardEvent matches a stored canonical key. */
export function keyMatches(e: KeyboardEvent, stored: string): boolean {
  if (!stored) return false;
  return eventToKey(e) === stored.toUpperCase();
}
